import { Typography, Grid, Paper, Box, IconButton, Tooltip } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useTranslation } from 'react-i18next';
import { GetNeedAgentStatsResponseDto } from '@bandido/shared/dtos';
import { 
  blue, 
  orange, 
  green, 
  purple, 
  blueGrey, 
  brown, 
  deepOrange 
} from '@mui/material/colors';
import { OriginatedByChannel } from '@bandido/shared/types';

interface NeedsAgentSectionProps {
  needAgentStats?: GetNeedAgentStatsResponseDto;
  onRefresh?: () => void;
}

export const NeedsAgentSection = ({ 
  needAgentStats,
  onRefresh 
}: NeedsAgentSectionProps) => {
  const { t } = useTranslation();

  const channelColors = {
    [OriginatedByChannel.WHATSAPP]: green[600],           
    [OriginatedByChannel.CALL]: blue[600],                
    [OriginatedByChannel.EMAIL]: orange[600],             
    [OriginatedByChannel.MANUAL]: purple[600],            
    [OriginatedByChannel.EXTERNAL_INTEGRATION]: blueGrey[600],
    [OriginatedByChannel.UPLOADED]: brown[600],           
    [OriginatedByChannel.SUPPORT_FORM]: deepOrange[600],  
  };

  const teamColors = [
    blue[400],
    orange[400],
    green[400],
    purple[400],
    blueGrey[400],
    brown[400],
    deepOrange[400],
  ];

  const getNeedAgentCasesByTeam = () => {
    if (!needAgentStats?.byTeam) return [];

    const allTeams = new Set<string>();
    needAgentStats.byTeam.forEach(agency => {
      agency.teams.forEach(team => {
        if (team.teamName) allTeams.add(team.teamName);
      });
    });

    return needAgentStats.byTeam.map(agency => {
      const agencyData: any = { agency: agency.agencyName };
      
      Array.from(allTeams).forEach(teamName => {
        agencyData[teamName] = 0;
      });
      
      agency.teams.forEach(team => {
        if (team.teamName) {
          agencyData[team.teamName] = team.count;
        }
      });
      
      return agencyData;
    });
  };

  const getNeedAgentCasesByChannel = () => {
    if (!needAgentStats?.byChannel) return [];

    const allChannels = new Set<string>();
    needAgentStats.byChannel.forEach(item => {
      allChannels.add(item.channel);
    });

    const agencyMap = new Map<string, Record<string, number>>();
    needAgentStats.byChannel.forEach(item => {
      if (!agencyMap.has(item.agencyName)) {
        const channelData: Record<string, number> = {};
        Array.from(allChannels).forEach(channel => {
          channelData[channel] = 0;
        });
        agencyMap.set(item.agencyName, channelData);
      }
      agencyMap.get(item.agencyName)![item.channel] = item.count;
    });

    return Array.from(agencyMap.entries()).map(([agencyName, channelData]) => ({
      agency: agencyName,
      ...channelData
    }));
  };

  const getTeamSeries = () => {
    if (!needAgentStats?.byTeam) return [];

    const teams = new Set<string>();
    needAgentStats.byTeam.forEach(agency => {
      agency.teams.forEach(team => {
        if (team.teamName) teams.add(team.teamName);
      });
    });

    return Array.from(teams).map((teamName, index) => ({
      dataKey: teamName,
      label: teamName,
      color: teamColors[index % teamColors.length],
    }));
  };

  const getChannelSeries = () => {
    if (!needAgentStats?.byChannel) return [];

    const channels = new Set<string>();
    needAgentStats.byChannel.forEach(item => {
      channels.add(item.channel);
    });

    return Array.from(channels).map((channelName) => ({
      dataKey: channelName,
      label: t(`cases.originatedByChannel.${channelName}`) || channelName,
      color: channelColors[channelName as OriginatedByChannel] || blueGrey[400],
    }));
  };

  const teamDataset = getNeedAgentCasesByTeam();
  const channelDataset = getNeedAgentCasesByChannel();
  const teamSeries = getTeamSeries();
  const channelSeries = getChannelSeries();

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('dashboard.sections.needsAgent')}
        </Typography>
        {onRefresh && (
          <Tooltip title={t('common.refreshData')}>
            <IconButton
              onClick={onRefresh}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.sections.needAgentByTeam')}
            </Typography>
            {teamDataset.length > 0 ? (
              <BarChart
                dataset={teamDataset}
                xAxis={[{ 
                  scaleType: 'band', 
                  dataKey: 'agency',
                  height: 60,
                  tickLabelStyle: {
                    angle: 30,
                    textAnchor: 'start'
                  }
                }]}
                series={teamSeries}
                width={500}
                height={300}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.sections.noDataAvailable')}
              </Typography>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.sections.needAgentByChannel')}
            </Typography>
            {channelDataset.length > 0 ? (
              <BarChart
                dataset={channelDataset}
                xAxis={[{ 
                  scaleType: 'band', 
                  dataKey: 'agency',
                  height: 60,
                  tickLabelStyle: {
                    angle: 30,
                    textAnchor: 'start'
                  }
                }]}
                series={channelSeries}
                width={500}
                height={300}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.sections.noDataAvailable')}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}; 